/**
 * Internal input detection helper.
 *
 * @deprecated This module is a temporary internal replacement for the
 * `what-input` npm package, which was removed to reduce external
 * dependencies. The goal is to migrate away from `data-whatinput` /
 * `data-whatintent` selectors entirely and use native CSS such as
 * `:focus-visible` instead. Until that migration is complete, this
 * module keeps existing styling functional by setting the same
 * `data-whatinput` and `data-whatintent` attributes on `<html>`.
 */

type InputType = 'initial' | 'keyboard' | 'mouse' | 'touch'

let currentInput: InputType = 'initial'
let currentIntent: InputType = 'initial'

let currentTimestamp = Date.now()

/** Keys to ignore – modifier keys that accompany pointer interactions. */
const ignoreMap = ['Shift', 'Control', 'Alt', 'Meta']

/** When non-empty only these keys trigger "keyboard" input detection. */
let specificMap: string[] = []

let isScrolling = false
const mousePos = { x: 0, y: 0 }

const formInputs = ['input', 'select', 'textarea']

let isSetUp = false

function setUp() {
  if (isSetUp) {
    return // stop here
  }
  isSetUp = true

  const passiveCapture = { passive: true, capture: true }

  if (window.PointerEvent) {
    window.addEventListener('pointerdown', setInput, true)
    window.addEventListener('pointermove', setIntent, passiveCapture)
  } else {
    window.addEventListener('mousemove', setIntent, passiveCapture)

    if ('ontouchstart' in window) {
      window.addEventListener('touchstart', setInput, passiveCapture)
      window.addEventListener('touchend', setInput, true)
    }
  }

  window.addEventListener('mousedown', setInput, true)
  window.addEventListener('wheel', setIntent, passiveCapture)
  window.addEventListener('keydown', setInput, true)
  window.addEventListener('keyup', setInput, true)

  doUpdate('input')
  doUpdate('intent')
}

function resolvePointerType(event: PointerEvent): InputType {
  if (event.pointerType === 'pen' || event.pointerType === 'touch') {
    return 'touch'
  }
  return 'mouse'
}

function resolveInputType(event: Event): InputType {
  const type = event.type
  if (type === 'keydown' || type === 'keyup') {
    return 'keyboard'
  }
  if (type === 'mousedown') {
    return 'mouse'
  }
  if (type === 'touchstart' || type === 'touchend') {
    return 'touch'
  }
  if (type === 'pointerdown') {
    return resolvePointerType(event as PointerEvent)
  }
  if (type === 'pointermove') {
    return resolvePointerType(event as PointerEvent)
  }
  if (type === 'mousemove' || type === 'wheel') {
    return 'mouse'
  }
  return 'mouse'
}

/**
 * Prevent touch from being overridden by mouse events that fire
 * shortly after a touch event (within 200 ms).
 */
function validateTouch(value: InputType): boolean {
  const now = Date.now()
  const isFalsePositive =
    value === 'mouse' &&
    currentInput === 'touch' &&
    now - currentTimestamp < 200

  currentTimestamp = now
  return isFalsePositive
}

function setInput(event: Event) {
  const value = resolveInputType(event)
  const eventKey = (event as KeyboardEvent).key

  if (value === 'keyboard' && eventKey) {
    const ignoreMatch =
      specificMap.length === 0 && !ignoreMap.includes(eventKey)
    const specificMatch =
      specificMap.length > 0 && specificMap.includes(eventKey)

    if (!ignoreMatch && !specificMatch) {
      return // stop here
    }
  }

  if (validateTouch(value)) {
    return // stop here
  }

  if (currentInput !== value) {
    currentInput = value
    doUpdate('input')
  }

  if (currentIntent !== value) {
    // Preserve intent for keyboard interaction with form fields
    const activeElem = document.activeElement
    if (
      activeElem?.nodeName &&
      (!formInputs.includes(activeElem.nodeName.toLowerCase()) ||
        (activeElem.nodeName.toLowerCase() === 'button' &&
          !activeElem.closest('form')))
    ) {
      currentIntent = value
      doUpdate('intent')
    }
  }
}

function detectScrolling(event: MouseEvent) {
  if (mousePos.x !== event.screenX || mousePos.y !== event.screenY) {
    isScrolling = false
    mousePos.x = event.screenX
    mousePos.y = event.screenY
  } else {
    isScrolling = true
  }
}

function setIntent(event: Event) {
  const value = resolveInputType(event)

  if (event.type === 'pointermove' || event.type === 'mousemove') {
    detectScrolling(event as MouseEvent)
  }

  const isWheelEvent =
    event.type === 'wheel' ||
    event.type === 'mousewheel' ||
    event.type === 'DOMMouseScroll'

  if (
    ((!isScrolling && !validateTouch(value)) ||
      (isScrolling && isWheelEvent)) &&
    currentIntent !== value
  ) {
    currentIntent = value
    doUpdate('intent')
  }
}

function doUpdate(which: 'input' | 'intent') {
  document.documentElement.setAttribute(
    `data-what${which}`,
    which === 'input' ? currentInput : currentIntent
  )
}

// -- Public API -------------------------------------------------------

/**
 * Set which specific keys should trigger "keyboard" input detection.
 * When set, only matching key names count as keyboard input.
 * Pass e.g. `['Tab']` for Tab-only, or `['Tab', 'ArrowLeft', 'ArrowRight']` for Tab + arrow keys.
 */
function specificKeys(arr: string[]) {
  specificMap = arr
}

// -- Init -------------------------------------------------------------

// Delay setup so event listeners and initial data-whatinput /
// data-whatintent attributes are not added during SSR hydration.
// The attributes are set on <html> (outside React's hydration root)
// but the event listeners should not fire until the page is
// interactive. In test environments, set up synchronously so
// assertions work immediately after dispatching events.
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  if (process.env.NODE_ENV === 'test') {
    setUp()
  } else if (typeof requestIdleCallback === 'function') {
    // eslint-disable-next-line compat/compat
    requestIdleCallback(setUp)
  } else {
    setTimeout(setUp, 0)
  }
}

const whatInput = { specificKeys }

export default whatInput
